import { type ErrorRecord } from 'src/hooks/useForm';
import { type EmailSettings } from 'src/services/emailDistribution/types/emailSettings';
import { type AustinCase } from 'src/services/incident/types';
import { type DownloadTWPAPayload } from 'src/services/jmr/types';
import { type Site } from 'src/services/sites/types';

// **** Constants **** //

export enum TemporaryWorkPlacementEnum {
  MATCH_ROLE_NO_MODIFICATIONS,
  MATCH_ROLE_WITH_MODIFICATIONS,
  NO_MATCH,
}

export enum SelectedEmailTypeEnum {
  AssociateRejectedPlacement = 'Associate Declined Placement',
  ApprovedOnsitePlacement = 'Approved Onsite Placement',
  NoOnsitePlacementLOA = 'Associate LOA',
  ReturnedtoFullDuty = 'Associate Returned to Full Duty',
  DLSReferral = 'DLS Referral',
}

export enum ReferralAndClaimsStatusEnum {
  HasReachedLimit,
  HasPermanentRestrictions,
  HasHadWorkRelatedClaimDenied,
}

export enum AgreeWithHealthCareRecommendationsEnum {
  DoesNotAgree,
  IsUncertain,
}

// **** Types **** //

export interface AccommodationStatus {
  status: string;
  id: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Shape returned by the accommodation-status endpoints. `decisionByLoginNeeded` is computed by the
 * backend (true for approval statuses) and gates the "who approved this" step of the update flow.
 */
export interface AccommodationStatusResponseDto extends Omit<AccommodationStatus, 'createdAt' | 'updatedAt'> {
  decisionByLoginNeeded: boolean;
}

export interface SelectedPlacementFormData {
  status: string;
  decisionByLoginNeeded: boolean;
  id: number;
  placementId: number;
  approver: string;
}

export interface DownloadTWPAForm extends Omit<DownloadTWPAPayload, 'workPlacementProvide'> {
  placementShifts: string;
  workPlacementProvide: TemporaryWorkPlacementEnum;
}

export interface PlacementDetailsRevision {
  austinUploadFailed: boolean;
  id: number;
  jmrFileName: string;
  statusId: number;
  revisedBy: string;
  revisionDate: number;
  status: {
    id: number;
    status: string;
  };
  dlsReferralReason?: {
    id: number;
    /** AKA dlsStatus */
    internalName: string;
    description: string;
  } | null;
  endAt: string;
  previousFc: string | null;
  decisionNotes: string | null;
}

export interface PlacementDetailsWorkRestriction {
  taskId: number;
  taskName: string;
  restrictedValue: string;
  measureUnit: string;
  yesNoTypeTask: boolean;
  hoursTypeTask: boolean;
  bodyLocation?: string | null;
}

export interface PlacementDetailsAppliedModification {
  id: string;
  taskId: number;
  taskName: string;
  modificationId: number;
  modificationName: string;
  freeFormText: string;
}

export interface PlacementDetailsAllocatedJobShift {
  accommodationId: number;
  allocatedHours: number;
  jobId: number;
  jobName: string;
  shiftId: number;
  shiftCode: string;
  jobType: string;
  appliedModifications: PlacementDetailsAppliedModification[];
  isForTierTwoPlusRoles: boolean;
  isTierTwoPlusJobModified: boolean;
  modificationNotes: string;
}

export interface PlacementDetails {
  accommodationStatus: {
    id: number;
    status: string;
  };
  accommodationType: string;
  allocatedJobShifts: PlacementDetailsAllocatedJobShift[];
  associateFullName: string;
  employeeId: number;
  personId: string;
  endAtDate: string;
  externalCaseId: string;
  fc: string;
  job: string;
  login: string;
  managerAlias: string;
  modifiedShiftLength: number;
  newestJmrFileName: string;
  requesterLogin: string;
  shiftCode: string;
  startAtDate: string;
  transferredAtDate: string;
  decisionByLogin: string;
  decisionRequestId: string | null;
  decisionHubLink: string | null;
  timeInPending: number;
  timeInTransferPending: number;
  revisions: PlacementDetailsRevision[];
  workRestrictions: PlacementDetailsWorkRestriction[];
  placementTime: number;
  totalAccommodatedDaysByAssociate: number;
  countryId: number;
  regionId: number;
  siteId: number;
  caseIdValid: boolean;
  jobsDisplayed: boolean;
  jobSuffix: string;
  previousFc: string;
  updatedAt: string;
  editedBy: string;
  nextStatuses: AccommodationStatusResponseDto[];
  site: Site;
}

export interface SendEmailFormData {
  emailType: SelectedEmailTypeEnum;
  emailRecipients: string;
  placementDetails: string;
  lastDayWorked: string;
  firstDayAbsent: string;
  dateOfPermanentRestrictions: string;
  agreeWithHealthCareRecommendations: AgreeWithHealthCareRecommendationsEnum;
  referralAndClaimsStatus: ReferralAndClaimsStatusEnum;
  actionsForHr: string;
  areaManagerActionsNeeded: string;
  firstDayReturn: string;
}

export interface EditAssociateInfoModalFormData {
  login: string;
  job?: string | null;
  employeeId: string;
  homeShift?: string | null;
  caseId: string;
  caseSource: string;
  fullName: string;
}

export interface getPlacementIdByCaseIdAndLoginPayload {
  type: string;
  caseId: string;
  login: string;
}

export type PlacementDetailsPageState = {
  placementId: number;
  selectedTab: string;
  placementDetails: PlacementDetails;
  isFetchingPlacementDetails: boolean;
  isUpdateStatusModalOpen: boolean;
  isOomdModalOpen: boolean;
  isEmailModalOpen: boolean;
  sendEmailFormData: SendEmailFormData;
  sendEmailFormErrors: ErrorRecord<SendEmailFormData>;
  isConfirmDeleteModalOpen: boolean;
  emailSettings?: EmailSettings;
  jmrS3Keys: object;
  isEditAssociateInfoModalOpen: boolean;
  editAssociateInfoModalFormData: EditAssociateInfoModalFormData;
  isEditAssociateInfoSaving: boolean;
  redirectUrl: string;
  austinCases: AustinCase[];
  bccEmailList: string[];
};
